import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, isDirectorUser } from '../../core/helpers';
import { sb } from '../../core/db';
import { KRAYA } from '../../core/constants';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES, BUSINESS_TYPES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { listClientDocs, uploadClientDoc, publicDocUrl, resolveDocSrc } from '../../storage';
import { createAuthFor } from '../../api';
import { generateTempPassword } from '../../core/helpers';
import { Icon } from '../../components/Icon';
import { CustomFieldInputs } from '../../components/CustomFieldInputs';


function CreateClientModal({ data, onClose }) {
  const { employees, clients, users, dbCreateClient, showToast, user, runSubmitting, org } = useApp();
  const defaultAssign = data?.defaultAssignTo || user?.id || employees[0]?.id || "";
  const [form, setForm] = useState({
    name: "", contactName: "", email: "", phone: "",
    password: generateTempPassword(),
    type: "Private Limited", assignedTo: defaultAssign, salesOwner: "",
    state: "", cin: "", gst: "", pan: "", incorporationDate: "", directorIds: [],
    customData: {},
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isEmployee = user?.role === "employee";
  // #12 Existing directors (portal person-logins) that can be linked to this company
  const directors = (Array.isArray(users) ? users : []).filter(u => isDirectorUser(u, clients));
  const toggleDirector = (id) => setForm(f => ({ ...f, directorIds: f.directorIds.includes(id) ? f.directorIds.filter(x=>x!==id) : [...f.directorIds, id] }));

  const save = async () => {
    if (!form.name)  { showToast("Client name is required", "error"); return; }
    if (!form.phone) { showToast("Mobile number is required", "error"); return; }
    if (!/^\d{10}$/.test(form.phone)) { showToast("Mobile number must be exactly 10 digits", "error"); return; }
    if (!form.email) { showToast("Email is required", "error"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { showToast("Enter a valid email address", "error"); return; }
    // Duplicate checks (users + existing clients)
    const emailLo = form.email.toLowerCase().trim();
    const nameLo = form.name.trim().replace(/\s+/g, ' ').toLowerCase();
    const allUsers = Array.isArray(users) ? users : [];
    const allClients = Array.isArray(clients) ? clients : [];
    if (allUsers.some(u => u.email && u.email.toLowerCase() === emailLo)
      || allClients.some(c => c.email && c.email.toLowerCase().trim() === emailLo)) {
      showToast("Email already registered — use a different email", "error"); return;
    }
    if (form.phone && (
      allUsers.some(u => u.phone && u.phone.trim() === form.phone.trim())
      || allClients.some(c => c.phone && c.phone.trim() === form.phone.trim())
    )) {
      showToast("Phone already registered — use a different number", "error"); return;
    }
    if (nameLo && allClients.some(c => c.name && c.name.trim().replace(/\s+/g, ' ').toLowerCase() === nameLo)) {
      showToast("Company name already exists — use a different name", "error"); return;
    }
    if (!form.password || form.password.length < 6) { showToast("Password must be at least 6 characters", "error"); return; }

    await runSubmitting('Creating client…', async () => {
    const newClient = {
      id: uuid(),
      clientNo: (() => {
      const maxNum = clients.reduce((mx, c) => { const n = parseInt((c.clientNo||"").split("-").pop())||0; return n>mx?n:mx; }, 0);
      return "FB-" + new Date().getFullYear() + "-" + String(maxNum + 1).padStart(3, "0");
    })().padStart(3,"0"),
      name: form.name, contactName: form.contactName,
      email: form.email, phone: form.phone,
      password: form.password,
      type: form.type, status: "active",
      assignedTo: form.assignedTo, salesOwner: form.salesOwner || null,
      state: form.state || null, cin: form.cin || "", gst: form.gst || "",
      incorporationDate: form.incorporationDate || null,
      customData: form.customData || {},
      totalBilling: 0, collected: 0, pending: 0, progress: 0,
      createdAt: today(),
    };

    KRAYA.welcomeClient(form.phone, form.contactName || form.name);

    // Save to DB (also updates state)
    await dbCreateClient(newClient);
    // #12 Link the selected existing directors to this new company
    for (const did of form.directorIds) {
      try { await sb.insert("user_companies", { user_id: did, company_id: newClient.id }); } catch {}
    }
    onClose();
    }, 'client');
  };

  return (
    <div className="modal-box" style={{ maxWidth: 580 }}>
      <div className="modal-head"><div className="modal-title">New Client</div><button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button></div>
      <div className="modal-body">
        <div className="form-grid">
          <div className="form-grid-2">
            <div className="f-group">
              <label className="f-label">Company / Client Name <span className="f-req">*</span></label>
              <input className="f-input" placeholder="e.g. TechSpark Solutions Pvt Ltd" value={form.name} onChange={e => set("name", e.target.value)} />
            </div>
            <div className="f-group">
              <label className="f-label">Business Type</label>
              <select className="f-select" value={form.type} onChange={e => set("type", e.target.value)}>
                {BUSINESS_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="f-group">
              <label className="f-label">Date of Incorporation / Registration</label>
              <input className="f-input" type="date" value={form.incorporationDate} onChange={e => set("incorporationDate", e.target.value)} />
              <div className="f-hint">Anchors one-time deadlines (INC-20A, first board meeting, ADT-1).</div>
            </div>
            <div className="f-group">
              <label className="f-label">Contact Person Name</label>
              <input className="f-input" placeholder="Director / Partner name" value={form.contactName} onChange={e => set("contactName", e.target.value)} />
            </div>
            <div className="f-group">
              <label className="f-label">Mobile Number <span className="f-req">*</span></label>
              <input className="f-input" placeholder="10-digit mobile" type="tel" value={form.phone} onChange={e => set("phone", e.target.value.replace(/\D/g,""))} maxLength={10} />
            </div>
            <div className="f-group">
              <label className="f-label">Email Address <span className="f-req">*</span></label>
              <input className="f-input" type="email" placeholder="client@email.com" value={form.email} onChange={e => set("email", e.target.value)} />
            </div>
            <div className="f-group">
              <label className="f-label">Login Password <span className="f-req">*</span></label>
              <input className="f-input" placeholder="Min 6 characters" value={form.password} onChange={e => set("password", e.target.value)} />
              <div className="f-hint">Client will use this to login. They can change it later.</div>
            </div>
            {!isEmployee && (
              <div className="f-group" style={{ gridColumn:"1/-1" }}>
                <label className="f-label">Execution Team Member <span className="f-req">*</span></label>
                {employees.length === 0 ? (
                  <div style={{ padding:"10px",background:"#FFF8EC",border:"1px solid rgba(201,161,74,.3)",borderRadius:8,fontSize:12,color:"var(--gold-dk)",display:"flex",alignItems:"center",gap:6 }}>
                    <Icon name="warning" size={14} /> No employees found. Please add a team member first from Team → Employees.
                  </div>
                ) : (
                  <select className="f-select" value={form.assignedTo} onChange={e => set("assignedTo", e.target.value)}>
                    <option value="">Select employee…</option>
                    {employees.map(e=><option key={e.id} value={e.id}>{e.name} ({e.role})</option>)}
                  </select>
                )}
                <div className="f-hint">This employee will see the client in their portal and handle execution.</div>
              </div>
            )}
            {!isEmployee && (
              <div className="f-group" style={{ gridColumn:"1/-1" }}>
                <label className="f-label">Sales Team Member</label>
                <select className="f-select" value={form.salesOwner} onChange={e => set("salesOwner", e.target.value)}>
                  <option value="">Select sales person…</option>
                  {employees.map(e=><option key={e.id} value={e.id}>{e.name} ({e.role})</option>)}
                </select>
                <div className="f-hint">Who brought in / owns this client commercially (optional).</div>
              </div>
            )}
            {!isEmployee && (
              <div className="f-group" style={{ gridColumn:"1/-1" }}>
                <label className="f-label">Directors (link existing portal logins)</label>
                {directors.length === 0 ? (
                  <div className="f-hint">No directors yet. Create them under Team → Directors, then you can link them here.</div>
                ) : (
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:6, maxHeight:150, overflow:"auto", padding:6, border:"1px solid var(--border)", borderRadius:8 }}>
                    {directors.map(d => (
                      <label key={d.id} style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, padding:"4px 6px", cursor:"pointer", borderRadius:6, background: form.directorIds.includes(d.id) ? "#EEF2FF" : "transparent" }}>
                        <input type="checkbox" checked={form.directorIds.includes(d.id)} onChange={()=>toggleDirector(d.id)} />
                        <span>{d.name} <span style={{ color:"var(--muted)", fontSize:11 }}>{d.email||d.phone}</span></span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="f-hint">Select one or more directors who can log in and see this company. You can change this later in Edit.</div>
              </div>
            )}
          </div>
          <CustomFieldInputs
            fields={org?.customFields?.clients || []}
            values={form.customData}
            onChange={v => set("customData", v)}
          />
          <div className="info-box" style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", marginTop: 4 }}>
            <span><Icon name="info" size={16} /></span>
            <div style={{ fontSize:12, color:"var(--blue)" }}>
              After creating, share these login details with the client:<br/>
              <strong>URL:</strong> {window.location.hostname}<br/>
              <strong>Email:</strong> {form.email || "their email"}<br/>
              <strong>Password:</strong> {form.password}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save}>Create Client & Set Login</button>
      </div>
    </div>
  );
}

// ─── Create Invoice Modal ─────────────────────────────────────────────



export { CreateClientModal };
